import React from 'react';
import PropTypes from 'prop-types';

import {
  Button,
  Flex,
  Grid,
  P,
  TextField,
  Toggle,
} from '@decisiv/ui-components';
import { t, Trans } from '@lingui/macro';
import { FormGroup } from './StyledComponents';
import FeatureInfoDrawer from './FeatureInfoDrawer';
import { secondsToHumanReadable } from '../../utils/dates';
import { MAX_CACHE_TTL_LENGTH } from '../../common/constants/maxLengthValues';

const IntegrationCacheSettings = ({
  edit,
  cacheEnabled,
  cacheTTL,
  showTtlField,
  errors,
  handleToggleCacheModal,
  handleShowTtlField,
  handleTextChange,
  validateCacheTTL,
  setCacheEnabled,
}) => {
  const handleChange = (e) => {
    if (e.target.value.length > MAX_CACHE_TTL_LENGTH) {
      return;
    }

    handleTextChange(e);
  };
  return (
    <>
      <Grid.Column span="2">
        <FeatureInfoDrawer
          heading={<Trans>Cache</Trans>}
          ariaLabel={t`More information about Cache`}
        >
          <p>
            <Trans>
              When enabled, successful responses are stored and reused for
              identical requests within the configured time-to-live (TTL),
              instead of calling the partner again for the same data. This
              reduces load on the partner and improves response times.
            </Trans>
          </p>
          <ul>
            <li>
              <Trans>
                Only successful responses are cached. A failed request always
                calls the partner again, so nothing broken gets served back from
                cache.
              </Trans>
            </li>
            <li>
              <Trans>
                Once the TTL expires, the next matching request bypasses the
                cache, calls the partner, and refreshes the stored response for
                the next TTL window.
              </Trans>
            </li>
            <li>
              <Trans>
                Use Clear Cache to discard all stored responses immediately.
                Every request then calls the partner directly until the cache
                repopulates. Clearing a large cache can create a sudden spike in
                traffic to the partner, so use caution on high-volume
                integrations.
              </Trans>
            </li>
          </ul>
        </FeatureInfoDrawer>
      </Grid.Column>
      <Grid.Column>
        {edit ? (
          <FormGroup key="cache">
            {cacheEnabled ? (
              <Grid.Column>
                <span className="spanDetail">
                  <P>Enabled</P>
                </span>
                <span>
                  <P>Yes</P>
                </span>
                <span className="spanDetail">
                  <P>Cache TTL</P>
                </span>
                <span>
                  <P>
                    {cacheTTL} seconds ({secondsToHumanReadable(cacheTTL)})
                  </P>
                </span>
                <Flex marginY={1}>
                  <Button
                    text={t`Clear Cache`}
                    kind="secondary"
                    size="small"
                    onClick={handleToggleCacheModal}
                  />
                </Flex>
              </Grid.Column>
            ) : (
              <Grid.Column>
                <span className="spanDetail">
                  <P>Enabled</P>
                </span>
                <span>
                  <P>No</P>
                </span>
              </Grid.Column>
            )}
          </FormGroup>
        ) : (
          <FormGroup key="cacheEnabled">
            <Toggle
              name="toggle"
              options={[
                { id: 'disabled', text: t`Disabled` },
                { id: 'enabled', text: t`Enabled` },
              ]}
              onChange={(e) => {
                setCacheEnabled(e === 'enabled');
                handleShowTtlField();
              }}
              selected={cacheEnabled ? 'enabled' : 'disabled'}
            />
            {showTtlField ? (
              <Flex style={{ marginTop: '0.375rem' }}>
                <TextField
                  label={t`Cache TTL in seconds`}
                  name={t`cache_ttl`}
                  required
                  type="number"
                  value={String(cacheTTL)}
                  onChange={handleChange}
                  onBlur={validateCacheTTL}
                  warningMessage={errors.cache_ttl}
                />
              </Flex>
            ) : null}
          </FormGroup>
        )}
      </Grid.Column>
    </>
  );
};

IntegrationCacheSettings.propTypes = {
  edit: PropTypes.bool.isRequired,
  cacheEnabled: PropTypes.bool.isRequired,
  cacheTTL: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  showTtlField: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  errors: PropTypes.object.isRequired,
  handleToggleCacheModal: PropTypes.func.isRequired,
  handleShowTtlField: PropTypes.func.isRequired,
  handleTextChange: PropTypes.func.isRequired,
  validateCacheTTL: PropTypes.func.isRequired,
  setCacheEnabled: PropTypes.func.isRequired,
};

export default IntegrationCacheSettings;
